import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";
import KehilatiContainer from './panels/KehilatiContainer';
import CommunityInfoContainer from './panels/CommunityInfoContainer';
import DailyLearningContainer from './panels/DailyLearningContainer';
import WeeklyCourseContainer from './panels/WeeklyCourseContainer';
import ZmanimContainerLong from './panels/ZmanimContainerLong';
import HeaderContainer from './panels/HeaderContainer';
import HolidayScheduleContainer from './panels/HolidayScheduleContainer';
import WeekScheduleContainer from './panels/WeekScheduleContainer';
import ShabbatAndCourseScheduleContainer from './panels/ShabbatAndCourseScheduleContainer';
import HolidayInfoContainer from './panels/HolidayInfoContainer';
import { useScheduleTransition } from '../../../hooks/useScheduleTransition';
import QrCodeContainer from './panels/QrCodeContainer';
import { getLocale, getTimeFormatOptions, formatTime, formatShiurTime } from '../../../utils/localeUtils';

const Container = styled.div`
  background: url('/images/Armon/fond.png');
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  width: 100%;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
`;

const DoubleSeparator = styled.div`
  height: 6px;
  margin: 20px auto;
  width: 70%;
  border-top: 2px solid #2D3653;
  border-bottom: 2px solid #2D3653;
`;

const BasePanel = styled.div`
  padding: 20px 40px 0 40px;
  display: flex;
  width: 432px;
  flex-direction: column;
  font-size: clamp(12px, 1vw, 16px);
  height: 100%;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 380px;
    height: auto;
    min-height: 300px;
    margin: 0;
    padding: 20px;
    background-size: 380px auto;
    background-position: top center;
  }
`;

const ZmanimPanel = styled(BasePanel)`
  grid-area: 1 / 4 / 2 / 5;
  background: url('/images/Armon/ornement long.png') no-repeat center top;
  background-size: cover;
  height: 876px;

  @media (max-width: 768px) {
    grid-area: auto;
    height: auto;
    padding: 30px;
    min-height: 800px;
  }
`;

const CoursePanel = styled(BasePanel)`
  grid-area: 1 / 1 / 2 / 2;
  background: url('/images/Armon/ornement fin.png') no-repeat center top;
  background-size: cover;
  height: 566px;

  @media (max-width: 768px) {
    grid-area: auto;
    height: auto;
    min-height: 500px;
  }
`;

const QrCodePanel = styled(BasePanel)`
  grid-area: 1 / 1 / 2 / 1;
  background: url('/images/Armon/ornement large.png') no-repeat center top;
  background-size: cover;
  height: 276px;
  margin-top: auto;

  @media (max-width: 768px) {
    grid-area: auto;
    height: auto;
    min-height: 240px;
    margin-top: 0;
  }
`;

const FullHeightSimplePanelCol2 = styled(BasePanel)`
  grid-area: 1 / 2 / 3 / 3;
  background: url('/images/Armon/ornement long.png') no-repeat center top;
  background-size: cover;
  height: 876px;

  @media (max-width: 768px) {
    grid-area: auto;
    height: auto;
    padding: 30px;
    min-height: 800px;
  }
`;

const FullHeightSimplePanelCol3 = styled(BasePanel)`
  grid-area: 1 / 3 / 3 / 4;
  background: url('/images/Armon/ornement long.png') no-repeat center top;
  background-size: cover;
  height: 876px;

  @media (max-width: 768px) {
    grid-area: auto;
    height: auto;
    padding: 30px;
    min-height: 800px;
  }
`;

const Separator = styled.div`
  height: 1px;
  background-color: rgba(45, 54, 83, 0.2);
  width: 90%;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(20px);
  }
`;

const AnimatedContent = styled.div`
  animation: ${({ $isEntering }) =>
    $isEntering
      ? css`${fadeIn} 0.5s ease-out forwards`
      : css`${fadeOut} 0.5s ease-out forwards`};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 67fr 33fr;
  column-gap: 30px;
  row-gap: 10px;
  margin: auto;
  height: calc(100vh - 150px);
  align-items: stretch;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    height: auto;
    margin: 0;
    padding: 10px;
    gap: 20px;
    align-items: center;
    
    /* Définir l'ordre d'affichage sur mobile */
    & > * {
      order: 5; /* Ordre par défaut */
    }

    /* Horaires Semaine */
    & > ${FullHeightSimplePanelCol2} {
      order: 1;
    }

    /* Horaires Shabbat */
    & > ${FullHeightSimplePanelCol3} {
      order: 2;
    }

    /* Cours */
    & > div:nth-last-child(9) {
      order: 4;
    }

    /* Prochain RDV */
    & > div:nth-last-child(4) {
      order: 5;
    }

    /* Zmanim */
    & > div:nth-last-child(8) {
      order: 6;
    }

    /* CommunityInfoContainer */
    & > div:nth-last-child(3) {
      order: 7;
    }

    /* Separator */
    & > div:nth-last-child(2) {
      order: 8;
    }

    /* KehilatiContainer */
    & > div:nth-last-child(1) {
      order: 9;
    }
  }
`;


const ArmonBaavad = ({ community, schedule, adImages, logo, sponsor, shiur, zmanim, dailyLearning, nextEvents, parasha, holidayInformation, language, omer, fontSizes }) => {
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(getLocale(language), getTimeFormatOptions(language))
  );
  // Mémoriser les options de formatage
  const timeFormatOptions = useMemo(() => getTimeFormatOptions(language), [language]);

  // Utilisation du hook personnalisé
  const { showHolidaySchedule, scheduleTransitioning } = useScheduleTransition(holidayInformation);

  const weekSchedule = schedule?.filter(s => {
    return s.category === 'Week';
  }) || [];

  const shabatSchedule = schedule?.filter(s => {
    return s.category === 'Shabbat';
  }) || [];

  const holidaySchedule = schedule?.filter(item => {
    return item.category !== 'Week' && item.category !== 'Shabbat';
  });

  useEffect(() => {
    let isSubscribed = true;

    const updateTime = () => {
      if (isSubscribed) {
        setCurrentTime(new Date().toLocaleTimeString(getLocale(language), timeFormatOptions));
      }
    };

    const timer = setInterval(updateTime, 60000);

    return () => {
      isSubscribed = false;
      clearInterval(timer);
    };
  }, [language, timeFormatOptions]);

  const HolidaySchedule = ({ isTransitioning, fontSize }) => {
    return (
      <FullHeightSimplePanelCol2>
        <HolidayScheduleContainer 
          holidaySchedule={holidaySchedule}
          isTransitioning={isTransitioning}
          AnimatedContent={AnimatedContent}
          displayRoom={false}
          fontSize={fontSize}
        />
      </FullHeightSimplePanelCol2>
    );
  };

  const WeekSchedule = ({ isTransitioning, fontSize }) => {
    return (
      <FullHeightSimplePanelCol2>
        <WeekScheduleContainer 
          weekSchedule={weekSchedule}
          isTransitioning={isTransitioning}
          AnimatedContent={AnimatedContent}
          displayRoom={false}
          isSliding={true}
          fontSize={fontSize}
        />
      </FullHeightSimplePanelCol2>
    );
  };

  const ShabbatAndCourseSchedule = ({ isTransitioning, fontSize }) => {
    return (
      <FullHeightSimplePanelCol3>
        <ShabbatAndCourseScheduleContainer 
          isTransitioning={isTransitioning}
          parasha={parasha}
          shabatSchedule={shabatSchedule}
          shiur={shiur}
          formatTime={(dateString) => formatTime(dateString, language)}
          formatShiurTime={formatShiurTime}
          AnimatedContent={AnimatedContent}
          displayRoom={false}
          fontSize={fontSize}
          community={community}
        />
      </FullHeightSimplePanelCol3>
    );
  };

  const HolidayInfo = ({ isTransitioning, information }) => {
    return (
      <FullHeightSimplePanelCol3>
        <HolidayInfoContainer 
          isTransitioning={isTransitioning}
          information={information}
          shiur={shiur}
          formatShiurTime={formatShiurTime}
          AnimatedContent={AnimatedContent}
        />
      </FullHeightSimplePanelCol3>
    );
  };
  return (
    <>
      <Container>
        <HeaderContainer 
          zmanim={zmanim}
          logo={logo}
          community={community}
          sponsor={sponsor}
          omer={omer}
        />
        
        <Grid>
          <CoursePanel>
            <WeeklyCourseContainer shiur={shiur} displayDay={false} displayRav={true} fontSize={fontSizes.Shiur} />
            <DoubleSeparator />
            <DailyLearningContainer dailyLearning={dailyLearning} fontSize={fontSizes.Shiur} />
          </CoursePanel>
          
          {/* Nouveau QrCode Panel */}
          <QrCodePanel>
            <QrCodeContainer
              qrCode={community?.qrCode}
              qrCodeText={community?.qrCodeText}
            />
          </QrCodePanel>

          {/* Première colonne - Horaires de la semaine */}
          {holidayInformation ? (
            showHolidaySchedule ? (
              <HolidaySchedule isTransitioning={scheduleTransitioning} fontSize={fontSizes.Holiday}/>
            ) : (
              <WeekSchedule isTransitioning={scheduleTransitioning} displayRoom={false} isSliding={true} fontSize={fontSizes.Week}/>
            )
          ) : (
            <WeekSchedule isTransitioning={false} displayRoom={false} isSliding={true} fontSize={fontSizes.Week}/>
          )}
          {/* Troisième colonne - Horaires de Chabbat et Cours */}
          {holidayInformation ? (
            showHolidaySchedule ? (
              <HolidayInfo 
                isTransitioning={scheduleTransitioning} 
                information={holidayInformation.information} 
              />
            ) : (
              <ShabbatAndCourseSchedule isTransitioning={scheduleTransitioning} fontSize={fontSizes.Shabbat}/>
            )
          ) : (
            <ShabbatAndCourseSchedule isTransitioning={false} fontSize={fontSizes.Shabbat}/>
          )}

          <ZmanimPanel>
            <ZmanimContainerLong zmanim={zmanim} />
          </ZmanimPanel>
          
          {/* Nouveaux éléments pour mobile */}
          <CommunityInfoContainer community={community} />
          <Separator />
          <KehilatiContainer
            gridArea="2 / 1 / 2 / 5"
          />

        </Grid>
      </Container>
    </>
  );
};

export default ArmonBaavad; 